/**
 * Whether a membership document grants class access right now. Identical to the
 * server's `isAccessGrantingEntitlement`, and the two copies have to move
 * together: dunning grace keeps access, while a dunning-paused document denies
 * whatever its status says, because writing off the episode invoice flips the
 * status back to "active" while collection is still stopped.
 *
 * No runtime dependencies, so mobile can import this too.
 */

/**
 * Structural rather than `UserBoxMembership`, so a raw document read is
 * assignable whatever shape the SDK produced.
 */
export interface AccessEntitlementLike {
  status?: unknown;
  dunningState?: unknown;
  [field: string]: unknown;
}

/** The statuses the access-gate query must load; `past_due` still faces the predicate. */
export const ACCESS_QUERY_STATUSES = ["active", "past_due"];

export const isAccessGrantingEntitlement = (entitlement?: AccessEntitlementLike | null): boolean =>
  !!entitlement &&
  entitlement.dunningState !== "paused" &&
  (entitlement.status === "active" ||
    (entitlement.status === "past_due" && entitlement.dunningState === "grace"));

/** The box's entitlements that grant access right now. */
export const accessGrantingForBox = <T extends AccessEntitlementLike & { boxID?: unknown }>(
  memberships: T[],
  boxID: string
): T[] => memberships.filter((m) => m.boxID === boxID && isAccessGrantingEntitlement(m));

/**
 * A membership stopped by dunning. Denies access on its own, but is what tells
 * "your payment failed" apart from "you never had a membership".
 */
export const isDunningPaused = (entitlement?: AccessEntitlementLike | null): boolean =>
  !!entitlement && entitlement.dunningState === "paused";
